import { useEffect, Suspense, lazy } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { useFormModal } from '../context/FormModalContext'
import { duration, ease } from '../lib/motion'
import InquiryForm from './InquiryForm'
import { partners } from '../data/partners'

// IntakeForm, PartnerApplicationForm, and FreeBuildForm are lazy-loaded
// (unlike InquiryForm above, which stays a normal static import): those
// three are ONLY ever rendered here, inside this modal, which starts
// closed — so their code isn't needed until someone actually opens one,
// and splitting them into separate chunks measurably shrinks the JS every
// visitor downloads and parses on page load. InquiryForm can't get the
// same treatment: it's also used as real static content on several
// campaign landing pages (rendered inline, server-side, on page load), so
// it's already required in the main bundle regardless — lazy-loading it
// here specifically would add Suspense complexity for zero actual byte
// savings.
const IntakeForm = lazy(() => import('./getawebsite/IntakeForm'))
const PartnerApplicationForm = lazy(() => import('./campaigns/PartnerApplicationForm'))
const FreeBuildForm = lazy(() => import('./FreeBuildForm'))

/**
 * The single modal shell mounted once at the app root. Styled after
 * SideShift's own onboarding popup: a dimmed, blurred backdrop; a
 * centered card on desktop that fades/scales in; a bottom sheet on
 * mobile that slides up instead (rounded top corners, near-full-height) —
 * same "one question, tap to continue" flow underneath either way, just
 * the container shape adapting to the viewport like SideShift's does.
 *
 * Deliberately dumb about *which* form it shows — it just reads `kind`
 * from FormModalContext and renders the matching form component with
 * `inModal` set, so each form's own submission logic (Netlify fetch,
 * tracking, redirect) stays exactly where it already lived.
 */
export default function FormModal() {
  const { kind, props, close } = useFormModal()
  const open = Boolean(kind)

  useEffect(() => {
    if (!open) return
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const onKeyDown = (e) => {
      if (e.key === 'Escape') close()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => {
      document.body.style.overflow = previousOverflow
      window.removeEventListener('keydown', onKeyDown)
    }
  }, [open, close])

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          key="form-modal-backdrop"
          className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: duration.fast, ease: ease.out }}
          style={{ background: 'color-mix(in srgb, black 55%, transparent)', backdropFilter: 'blur(6px)' }}
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) close()
          }}
        >
          <motion.div
            key={kind}
            role="dialog"
            aria-modal="true"
            initial={{ opacity: 0, y: 32, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.98 }}
            transition={{ duration: duration.fast, ease: ease.out }}
            className="relative w-full sm:max-w-lg max-h-[92vh] sm:max-h-[88vh] overflow-y-auto rounded-t-3xl sm:rounded-3xl border"
            style={{ background: 'var(--bg)', borderColor: 'var(--border)' }}
          >
            <button
              type="button"
              onClick={close}
              aria-label="Close"
              className="absolute top-4 right-4 z-10 flex items-center justify-center rounded-full"
              style={{
                width: 32,
                height: 32,
                background: 'var(--bg-elevated)',
                border: '1px solid var(--border)',
                color: 'var(--text-secondary)',
              }}
            >
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                <path d="M1 1l12 12M13 1L1 13" />
              </svg>
            </button>

            <div className="px-6 pt-8 pb-6 sm:px-8 sm:pt-10 sm:pb-8">
              {kind === 'inquiry' && (
                <InquiryForm
                  inModal
                  selectedTier={props.selectedTier ?? null}
                  onSelectTier={props.onSelectTier}
                  promise={props.promise ?? 'strategy'}
                  selectedGrowthPlan={props.selectedGrowthPlan ?? null}
                  onSuccess={close}
                  onRequestClose={close}
                />
              )}
              {(kind === 'intake' || kind === 'partner' || kind === 'freeBuild') && (
                <Suspense
                  fallback={
                    <div className="py-16 flex items-center justify-center" style={{ color: 'var(--text-muted)' }}>
                      <span className="text-sm">Loading…</span>
                    </div>
                  }
                >
                  {kind === 'intake' && <IntakeForm inModal onSuccess={close} />}
                  {kind === 'partner' && (
                    <PartnerApplicationForm inModal config={partners.application} onSuccess={close} />
                  )}
                  {kind === 'freeBuild' && <FreeBuildForm inModal onSuccess={close} />}
                </Suspense>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
